// Agency-home queries (the agency home design doc (CommitMind) P3).
//
// Lives outside api.ts on purpose — api.ts is a 5000+ line god-file and
// every addition fans into review pain. The agency-home work is a
// self-contained track; keeping its fetchers + queryOptions co-located
// here makes P3-P9 reviewable on their own and lets api.ts stay
// untouched while parallel work lands there.
//
// When P9 deletes the old /home, this module + /api/home/* routes become
// the canonical surface; api.ts's feedQuery / fetchFeed get retired.

import { queryOptions } from '@tanstack/react-query';
import type { BrandMemoDTO, FeedCard } from './api.js';
import { env } from './env.js';

export interface DecisionsResponse {
  cards: FeedCard[];
  brandId: string;
}

export interface DecisionsCountResponse {
  brandId: string;
  /** Sum of decisionsCount + stuckCount — what the brand-switcher
   *  badge renders. Both feed into "things mag's asking the founder
   *  to act on, on this brand". */
  count: number;
  decisionsCount: number;
  stuckCount: number;
  /** [spec 3a365634] How many CAMPAIGNS have an open ask filed against
   *  them — campaigns, not cards, because the nav dot answers "is there
   *  a campaign wanting me" and three cards on one campaign is still one
   *  place to go.
   *
   *  Not part of `count`: that badge already counts the same decision
   *  cards, so adding this would double-count them. */
  campaignsNeedingYou?: number;
}

export interface ReceiptsResponse {
  cards: FeedCard[];
  brandId: string;
}

export interface StuckCampaignDTO {
  campaignId: string;
  campaignName: string | null;
  /** Detection branch — 'paused' means mag autopaused >24h ago and
   *  nobody resumed; 'spend_flat' means the campaign's live but spent
   *  $0 over the last 24h after prior week had spend (ad-account
   *  outage / depleted budget / scope gate). */
  reasonKind: 'paused' | 'spend_flat';
  /** When the stuck state began. */
  since: string;
  /** mag-voice pause reason when present (paused branch only).
   *  null for spend_flat — renderer templates a copy. */
  reason: string | null;
}

export interface StuckResponse {
  stuck: StuckCampaignDTO[];
  brandId: string;
}

export interface BriefRefreshResponse {
  outcome: 'memo_created' | 'already_memoed' | 'no_brand_brief' | 'brand_too_new';
  memo: BrandMemoDTO | null;
  brandId: string;
  costUsd: number;
}

/** Run the weekly memo generator for the active brand on demand —
 *  founder hits "↘ anything new since?" on /home and we run the same
 *  agent the Monday scheduler does. Already-current weeks no-op
 *  (dedup by period_start). Returns the freshest memo so the
 *  dashboard can update the brief in place without a separate
 *  fetch. */
export async function refreshBrief(brandId: string | null): Promise<BriefRefreshResponse> {
  const search = brandId ? `?brand=${encodeURIComponent(brandId)}` : '';
  const res = await fetch(`${env.API_URL}/api/home/brief/refresh${search}`, {
    method: 'POST',
    credentials: 'include',
  });
  if (!res.ok) {
    const detail = await safeReadDetail(res);
    throw new Error(`brief refresh failed (${res.status})${detail ? `: ${detail}` : ''}`);
  }
  return (await res.json()) as BriefRefreshResponse;
}

/** Open decision cards for the active brand. Used by AgencyDecisionsSection
 *  on /home. The server applies the brand=<id> scope and returns only
 *  cards where classification='decision' and status='open'. */
async function fetchDecisions(brandId: string | null): Promise<DecisionsResponse> {
  const search = brandId ? `?brand=${encodeURIComponent(brandId)}` : '';
  const res = await fetch(`${env.API_URL}/api/home/decisions${search}`, {
    credentials: 'include',
  });
  if (!res.ok) {
    const detail = await safeReadDetail(res);
    throw new Error(`decisions fetch failed (${res.status})${detail ? `: ${detail}` : ''}`);
  }
  return (await res.json()) as DecisionsResponse;
}

/** Recent receipt cards for the active brand — mag's record of what
 *  she did, with verdict. Used by AgencyReceiptsSection. */
async function fetchReceipts(brandId: string | null): Promise<ReceiptsResponse> {
  const search = brandId ? `?brand=${encodeURIComponent(brandId)}` : '';
  const res = await fetch(`${env.API_URL}/api/home/receipts${search}`, {
    credentials: 'include',
  });
  if (!res.ok) {
    const detail = await safeReadDetail(res);
    throw new Error(`receipts fetch failed (${res.status})${detail ? `: ${detail}` : ''}`);
  }
  return (await res.json()) as ReceiptsResponse;
}

/** Stuck campaigns for the active brand — campaigns mag paused that
 *  nobody resumed >24h ago. Surfaces above the running section with
 *  inline [resolve →] action per design doc §"What's running". */
async function fetchStuck(brandId: string | null): Promise<StuckResponse> {
  const search = brandId ? `?brand=${encodeURIComponent(brandId)}` : '';
  const res = await fetch(`${env.API_URL}/api/home/stuck${search}`, {
    credentials: 'include',
  });
  if (!res.ok) {
    const detail = await safeReadDetail(res);
    throw new Error(`stuck fetch failed (${res.status})${detail ? `: ${detail}` : ''}`);
  }
  return (await res.json()) as StuckResponse;
}

/** Lightweight per-brand decision count for the top-nav brand-switcher
 *  badge. Fired once per brand listed in the switcher dropdown — keep
 *  it cheap on the server (count(*) over the covering index). */
async function fetchBrandDecisionCount(brandId: string): Promise<DecisionsCountResponse> {
  const res = await fetch(`${env.API_URL}/api/brands/${brandId}/decisions/count`, {
    credentials: 'include',
  });
  if (!res.ok) {
    const detail = await safeReadDetail(res);
    throw new Error(`decisions count failed (${res.status})${detail ? `: ${detail}` : ''}`);
  }
  return (await res.json()) as DecisionsCountResponse;
}

async function safeReadDetail(res: Response): Promise<string | null> {
  try {
    const body = (await res.json()) as { detail?: string; error?: string };
    return body.detail ?? body.error ?? null;
  } catch {
    return null;
  }
}

/** Decisions queue for /home — see the agency home design doc (CommitMind) §"Needs
 *  your call". Refetches on focus (default) so a founder coming back
 *  to the tab sees mag's latest asks without a manual reload.
 *  refetchInterval keeps a foreground tab honest within 60s — mag
 *  proposers fire at their own cadence and the queue shouldn't lag
 *  the founder's attention by more than that. */
export const homeDecisionsQuery = (brandId: string | null) =>
  queryOptions({
    queryKey: ['home-decisions', brandId] as const,
    queryFn: () => fetchDecisions(brandId),
    refetchInterval: 60_000,
    refetchIntervalInBackground: false,
  });

/** Per-brand decision count for the brand-switcher badge. Per-brand
 *  query key so the switcher renders one badge per brand without
 *  refetching the whole list on brand change. 30s stale window —
 *  the badge isn't worth a /home-sized refetch storm but also
 *  shouldn't lag minutes behind reality. */
export const brandDecisionCountQuery = (brandId: string) =>
  queryOptions({
    queryKey: ['brand-decision-count', brandId] as const,
    queryFn: () => fetchBrandDecisionCount(brandId),
    staleTime: 30_000,
  });

/** Receipts panel for /home — see the agency home design doc (CommitMind)
 *  §"Receipts". Mag's record of what she did, with verdict. The
 *  dashboard composes this with the legacy autopilot audit feed
 *  during the P2→P9 cutover so brands without any classified receipt
 *  cards yet still see something. Same 60s foreground refresh as
 *  the decisions queue. */
export const homeReceiptsQuery = (brandId: string | null) =>
  queryOptions({
    queryKey: ['home-receipts', brandId] as const,
    queryFn: () => fetchReceipts(brandId),
    refetchInterval: 60_000,
    refetchIntervalInBackground: false,
  });

/** Stuck campaigns for /home's running section — campaigns mag paused
 *  >24h ago without resume. 60s foreground refresh. Per design doc
 *  §"What's running" these are the only running items with inline
 *  actions; the dashboard surfaces them ABOVE the running list with
 *  a loud `stuck` chip. */
export const homeStuckQuery = (brandId: string | null) =>
  queryOptions({
    queryKey: ['home-stuck', brandId] as const,
    queryFn: () => fetchStuck(brandId),
    refetchInterval: 60_000,
    refetchIntervalInBackground: false,
  });
